// Scheduling is anchored to Athens (Europe/Athens) wall-clock time, independent of the admin's own
// device timezone — so "publish at 16:00" means 16:00 in Greece whether the admin is in Athens, on a
// laptop set to UTC, or abroad. These helpers convert between an Athens wall-clock
// <input type="datetime-local"> value ("YYYY-MM-DDTHH:mm") and the UTC ISO instant we store, using the
// browser's Intl timezone database (the same Europe/Athens approach the worker's daily digest uses)
// rather than the device's own offset. EET/EEST daylight saving is handled automatically.

const ATHENS = 'Europe/Athens';
const pad = (n: number) => String(n).padStart(2, '0');

interface Parts { year: number; month: number; day: number; hour: number; minute: number; second: number }

/** the calendar/time parts of an instant as read in Athens local time. */
function athensParts(d: Date): Parts {
  const o: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat('en-GB', {
    timeZone: ATHENS, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(d)) {
    if (p.type !== 'literal') o[p.type] = p.value;
  }
  const hour = o.hour === '24' ? 0 : +(o.hour ?? '0'); // some engines emit '24' for midnight
  return {
    year: +(o.year ?? '1970'), month: +(o.month ?? '1'), day: +(o.day ?? '1'),
    hour, minute: +(o.minute ?? '0'), second: +(o.second ?? '0'),
  };
}

/** Athens − UTC offset (ms) at instant d (handles the EET/EEST daylight-saving shift). */
function athensOffsetMs(d: Date): number {
  const p = athensParts(d);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - d.getTime();
}

/** UTC ISO instant for an Athens wall-clock <input type="datetime-local"> value ("YYYY-MM-DDTHH:mm"). */
export function athensInputToIso(v: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(v || '');
  if (!m) return '';
  const guess = Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!, 0);
  // two passes so a value sitting right on a DST boundary still resolves to the correct instant.
  let utc = guess - athensOffsetMs(new Date(guess));
  utc = guess - athensOffsetMs(new Date(utc));
  return new Date(utc).toISOString();
}

/** Athens wall-clock <input type="datetime-local"> value ("YYYY-MM-DDTHH:mm") for a stored UTC ISO. */
export function isoToAthensInput(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(+d)) return '';
  const p = athensParts(d);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/** Human Athens date + time for a stored UTC ISO, e.g. "01 Oct 2026, 16:00". */
export function formatAthens(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(+d)) return '';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: ATHENS, day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(d);
}
